// 前后端共享类型定义

export interface AgentSnapshot {
  id: string;
  name: string;
  gender: string; // "male" / "female" / ""(未知或未设置)
  x: number;
  y: number;
  color: string;
  hat: string;
  activity: string; // 当前行为描述
  emoji: string; // 当前行为图标
  bubble: string | null; // 说话气泡
  isPlayer: boolean;
  isNPC: boolean;
  moving: boolean;
  sleeping: boolean; // 夜间睡觉中(走回家途中为 true,但显示走路;到家后才显示 zzz)
  dir: "up" | "down" | "left" | "right";
  mapId?: string;   // 所在地图 id(town / 室内场景)
  inRoom?: string;  // 室内房间 id(非室内为空)
  needs?: Needs;    // 需求快照(需求条用)
  targetId?: string | null; // 指令找人:正在追踪的目标角色 id(调试/观测用)
}

export interface WorldState {
  width: number;
  height: number;
  tiles: number[]; // 渲染用 tile 编码
  day: number;
  gameMin: number; // 当天第几分钟 0-1439
  weather: string; // sunny / cloudy / rain
  llmActive: boolean;
  online: number;
  mapId: string;   // 当前活跃地图 id
  facilities: FacilitySnapshot[]; // 设施占用/状态快照
}

export interface Needs {
  hunger: number;   // 饥饿
  energy: number;   // 精力
  hygiene: number;  // 清洁
  social: number;   // 社交
  mood: number;     // 心情
}

export interface FacilityDef {
  id: string;
  mapId: string;
  roomId: string;
  name: string;
  type: string;   // bed/stove/fridge/table/shower/treadmill/computer/desk/sofa/tv/piano/guitar/dressing_table/livestream/yoga_mat ...
  x: number;
  y: number;
  state?: string; // off / on / in_use
}

export interface FacilitySnapshot {
  id: string;
  mapId: string;
  roomId: string;
  name: string;
  type: string;
  x: number;
  y: number;
  state: string;
  occupierId: string | null;
  occupiedUntil: number;
}

export interface MapInfo {
  id: string;
  name: string;
  width: number;
  height: number;
}

export interface PerceptionObject {
  facilityId: string;
  name: string;
  type: string;
  state: string;
  occupiedBy?: string;
}

export interface Perception {
  mapId: string;
  roomId: string;
  roomName: string;
  objects: PerceptionObject[];
  nearbyAgents: { id: string; name: string; activity: string; visibleMood: string }[];
  visibleEmotion: string; // 自己当前可见神态(供他人观察),如"神情疲惫"
}

export interface Post {
  id: number;
  agentId: string | null;
  agentName: string;
  agentColor: string;
  kind: "diary" | "goal" | "highlight" | "system" | "player";
  content: string;
  day: number;
  gameMin: number;
  createdAt: number;
}

export interface AgentProfile {
  id: string;
  name: string;
  isNPC: boolean;
  color: string;
  hat: string;
  personality: string;
  backstory: string;
  wish: string;
  occupation: string;
  fame: number;
  coin: number;
  todayCoin: number;
  professionCategory: string;
  todayFame: number;
  goalLong: string;
  goalShort: string;
  activity: string;
  mapId: string;
  inRoom: string;
  gender: string;
  personalityScores?: {
    day: number;
    base: Record<string, number>;
    scores: Record<string, number>;
  };
  needs: Needs;
  memories: { content: string; day: number; gameMin: number; type: string }[];
  memoriesTotal: number;
  eventRecords?: {
    eventId: number;
    type: string;
    seq: number;
    name: string;
    day: number;
    status: string;
    role: string;
    detail: string;
  }[];
}

export interface BoardMessage {
  id: number;
  agentId: string | null;
  name: string;
  content: string;
  ts: number;
  isGuest: boolean;
}

export interface BoardQuota {
  isGuest: boolean;
  limit: number;
  used: number;
}

export interface ChatMsg {
  id: number;
  role: "user" | "agent";
  content: string;
  createdAt: number;
}

export interface Building {
  id: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  doorX: number;
  doorY: number;
  color: string;
  emoji: string;
  desc: string;
  open: boolean; // 是否可进入(露天区域)
  entrance?: boolean; // 是否室内地图入口(门可切换进入室内,如公寓大门)
}

// tile 编码
export const T = {
  GRASS: 0,
  PATH: 1,
  WATER: 2,
  TREE: 3,
  FLOWER: 4,
  FARM: 5,
  WALL: 6,
  DOOR: 7,
  FOUNTAIN: 8,
  STONE: 9,
  SAND: 10,
  CAMPFIRE: 11,
  BENCH: 12,
  CROPS: 13,
  STUMP: 14,
  // 室内基础(15-19)
  WOOD_FLOOR: 15,
  TILE_FLOOR: 16,
  CARPET: 17,
  INNER_WALL: 18,
  ROOM_DOOR: 19,
  // 室内家具(20-29)
  BED: 20,
  STOVE: 21,
  FRIDGE: 22,
  TABLE: 23,
  SHOWER: 24,
  TREADMILL: 25,
  COMPUTER: 26,
  DESK: 27,
  SOFA: 28,
  TV: 29,
  // 参考场景扩展(30-34)
  PIANO: 30,
  GUITAR: 31,
  DRESSING_TABLE: 32,
  LIVESTREAM: 33,
  YOGA_MAT: 34,
} as const;

export const WEATHERS: Record<string, { name: string; emoji: string }> = {
  sunny: { name: "晴朗", emoji: "☀️" },
  cloudy: { name: "多云", emoji: "⛅" },
  rain: { name: "细雨", emoji: "🌧️" },
};

// ---------- 小镇日报 ----------
export interface DailyReportEvent {
  time: string;
  kind: string;
  content: string;
}

export interface DailyReportSummary {
  day: number;
  generatedAt: number;
  weather: string;
  title: string;
  summary: string;
}

export interface DailyReport {
  day: number;
  generatedAt: number;
  weather: string;
  population: number;
  title: string;
  summary: string;
  narrative?: string;
  stats: {
    events: number;
    conversations: number;
    thoughts: number;
    activities: number;
    relationshipEvents: number;
    diaries: number;
    playerChats: number;
  };
  events: DailyReportEvent[];
  conversations: string[];
  activities: { name: string; count: number }[];
  relationshipEvents: { time: string; desc: string }[];
}
